import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export type SessionStatus = 'recording' | 'uploading' | 'completed' | 'failed'

export interface RecordingSession {
  id: string
  appointmentId?: string
  /** Form-submission owner (form/dossier recording path). Set when there is no appointment. */
  formSubmissionId?: string
  /** Expected speaker count hint for diarization, captured at recording start. */
  speakerCount?: number
  /**
   * Every streaming transcript this recording produced, in the order the
   * WebSocket `ready` messages arrived. One recording yields more than one
   * whenever its socket is replaced — a reconnect or a planned rotation (#3293)
   * — and each is a separate server-side transcript the local audio supersedes.
   *
   * This is a list rather than a pointer because it used to be one: a single
   * field rewritten on every `ready` meant the upload path deleted whichever
   * attempt was *newest*, including one that was still recording (#3676).
   */
  streamingTranscriptIds: string[]
  startedAt: number
  status: SessionStatus
  totalChunks: number
}

export interface AudioChunk {
  sessionId: string
  sequence: number
  blob: Blob
  timestamp: number
}

interface RecordingDBSchema extends DBSchema {
  sessions: {
    key: string
    value: RecordingSession
    indexes: {
      'by-status': SessionStatus
      'by-appointment': string
      'by-form-submission': string
    }
  }
  chunks: {
    key: [string, number] // [sessionId, sequence]
    value: AudioChunk
    indexes: {
      'by-session': string
    }
  }
}

const DB_NAME = 'scrivio-recordings'
const DB_VERSION = 3

/**
 * A session row as the v3 upgrade finds it: a pre-v3 row carries at most one
 * `streamingTranscriptId`, an already-migrated one carries the list.
 */
type UpgradingRecordingSession = Omit<RecordingSession, 'streamingTranscriptIds'> & {
  streamingTranscriptId?: string
  streamingTranscriptIds?: string[]
}

/**
 * The slice of `idb`'s version-change cursor the v3 migration drives. Declared
 * structurally so the migration can be exercised without a real IndexedDB.
 */
interface UpgradeCursor {
  value: UpgradingRecordingSession
  update(value: UpgradingRecordingSession): Promise<unknown>
  continue(): Promise<UpgradeCursor | null>
}

interface UpgradeSessionStore {
  openCursor(): Promise<UpgradeCursor | null>
}

/**
 * Rewrite every stored session onto `streamingTranscriptIds`, carrying over the
 * single `streamingTranscriptId` a pre-v3 row held.
 */
export async function migrateSessionsToTranscriptIdList(
  store: UpgradeSessionStore
): Promise<void> {
  let cursor = await store.openCursor()
  while (cursor) {
    const { streamingTranscriptId, streamingTranscriptIds, ...session } = cursor.value
    await cursor.update({
      ...session,
      streamingTranscriptIds:
        streamingTranscriptIds ?? (streamingTranscriptId ? [streamingTranscriptId] : []),
    })
    cursor = await cursor.continue()
  }
}

let dbInstance: IDBPDatabase<RecordingDBSchema> | null = null

/**
 * Get or create the IndexedDB database instance.
 * Uses singleton pattern to avoid multiple connections.
 */
async function getDatabase(): Promise<IDBPDatabase<RecordingDBSchema>> {
  if (dbInstance) {
    return dbInstance
  }

  dbInstance = await openDB<RecordingDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, tx) {
      if (oldVersion < 1) {
        // Create sessions store
        const sessionsStore = db.createObjectStore('sessions', { keyPath: 'id' })
        sessionsStore.createIndex('by-status', 'status')
        sessionsStore.createIndex('by-appointment', 'appointmentId')

        // Create chunks store with composite key [sessionId, sequence]
        const chunksStore = db.createObjectStore('chunks', { keyPath: ['sessionId', 'sequence'] })
        chunksStore.createIndex('by-session', 'sessionId')
      }

      if (oldVersion < 2) {
        // v2: index sessions by their form-submission owner so the recovery UI
        // can locate form/dossier-path recordings, not just appointment-path ones.
        tx.objectStore('sessions').createIndex('by-form-submission', 'formSubmissionId')
      }

      if (oldVersion < 3) {
        // v3: `streamingTranscriptId` (one, overwritten on every `ready`) becomes
        // `streamingTranscriptIds` (all of them, #3676). Migrated rather than read
        // from both shapes: a session still in IndexedDB at upgrade time is one an
        // interrupted recording left behind, and it is exactly those the recovery
        // upload has to be able to supersede.
        // Not awaited — `upgrade` is synchronous by contract, and every step is
        // an IndexedDB request, so the version-change transaction stays open
        // until the walk finishes and commits atomically. A failed request aborts
        // it, which fails `openDB` as a whole rather than leaving half-migrated
        // rows behind; catching here only keeps that from surfacing a second time
        // as an unhandled rejection.
        migrateSessionsToTranscriptIdList(
          tx.objectStore('sessions') as unknown as UpgradeSessionStore
        ).catch((err) => {
          console.warn('Failed to migrate recording sessions to v3:', err)
        })
      }
    },
    blocked() {
      console.warn('IndexedDB blocked: another connection is open')
    },
    blocking() {
      console.warn('IndexedDB blocking: this connection is blocking another')
      dbInstance?.close()
      dbInstance = null
    },
    terminated() {
      // Browser-initiated close (tab suspension, storage eviction, OS sleep) —
      // outside our control and recovered automatically by dropping the
      // singleton so the next getDatabase() reopens. Warn, don't error: a
      // console.error here would page as a Sentry error event (SCRIVIO-FE-2B).
      console.warn('IndexedDB connection terminated unexpectedly; will reopen on next use')
      dbInstance = null
    },
  })

  return dbInstance
}

/**
 * Generate a unique session ID.
 */
function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/**
 * Extra metadata captured when a recording session starts.
 */
export interface CreateSessionOptions {
  /** Form-submission owner (used on the form/dossier path when no appointment). */
  formSubmissionId?: string
  /** Expected speaker count hint for diarization. */
  speakerCount?: number
}

/**
 * Create a new recording session.
 *
 * @param appointmentId - Optional appointment to associate with the recording
 * @param options - Optional form-submission owner and speaker-count hint
 * @returns The created session
 */
export async function createSession(
  appointmentId?: string,
  options: CreateSessionOptions = {}
): Promise<RecordingSession> {
  const db = await getDatabase()

  const session: RecordingSession = {
    id: generateSessionId(),
    appointmentId,
    formSubmissionId: options.formSubmissionId,
    speakerCount: options.speakerCount,
    streamingTranscriptIds: [],
    startedAt: Date.now(),
    status: 'recording',
    totalChunks: 0,
  }

  await db.put('sessions', session)
  return session
}

/**
 * Get a session by ID.
 *
 * @param sessionId - The session ID
 * @returns The session or undefined if not found
 */
export async function getSession(sessionId: string): Promise<RecordingSession | undefined> {
  const db = await getDatabase()
  return db.get('sessions', sessionId)
}

/**
 * Update session status.
 *
 * @param sessionId - The session ID
 * @param status - The new status
 */
export async function updateSessionStatus(
  sessionId: string,
  status: SessionStatus
): Promise<void> {
  const db = await getDatabase()
  const session = await db.get('sessions', sessionId)

  if (!session) {
    throw new Error(`Session not found: ${sessionId}`)
  }

  session.status = status
  await db.put('sessions', session)
}

/**
 * Record a streaming transcript this session produced (from the WebSocket
 * `ready` message), linking the local session to its server-side transcripts.
 *
 * Appends rather than replaces: every socket a recording goes through creates
 * its own transcript, and the local audio supersedes all of them. Overwriting
 * left the upload path pointing at whichever attempt was newest — including one
 * that was still recording (#3676). Re-recording the same id is a no-op.
 *
 * @param sessionId - The session ID
 * @param streamingTranscriptId - The transcript id received from the server
 */
export async function addStreamingTranscriptId(
  sessionId: string,
  streamingTranscriptId: string
): Promise<void> {
  const db = await getDatabase()
  const session = await db.get('sessions', sessionId)

  if (!session) {
    throw new Error(`Session not found: ${sessionId}`)
  }

  if (session.streamingTranscriptIds.includes(streamingTranscriptId)) {
    return
  }

  session.streamingTranscriptIds = [...session.streamingTranscriptIds, streamingTranscriptId]
  await db.put('sessions', session)
}

/**
 * Add an audio chunk to a session.
 * Automatically assigns the next sequence number.
 *
 * A chunk carries no length of its own. It used to, and the value was the
 * recorder's flush *interval* rather than the audio's length — never read, and
 * wrong for anyone who did read it, since the last chunk of a recording holds
 * less than a full interval (#3868). What a duration is derived from is the
 * session's chunk count and `STORAGE_CHUNK_INTERVAL_MS`, which says so (#3869).
 *
 * @param sessionId - The session ID
 * @param blob - The audio data
 * @returns The stored chunk with assigned sequence number
 */
export async function addChunk(sessionId: string, blob: Blob): Promise<AudioChunk> {
  const db = await getDatabase()

  // Get session and increment chunk count
  const session = await db.get('sessions', sessionId)
  if (!session) {
    throw new Error(`Session not found: ${sessionId}`)
  }

  const sequence = session.totalChunks
  session.totalChunks++

  const chunk: AudioChunk = {
    sessionId,
    sequence,
    blob,
    timestamp: Date.now(),
  }

  // Use transaction to ensure atomicity
  const tx = db.transaction(['sessions', 'chunks'], 'readwrite')
  await Promise.all([tx.objectStore('sessions').put(session), tx.objectStore('chunks').put(chunk)])
  await tx.done

  return chunk
}

/**
 * Get all chunks for a session, ordered by sequence.
 *
 * @param sessionId - The session ID
 * @returns Array of chunks in sequence order
 */
export async function getChunks(sessionId: string): Promise<AudioChunk[]> {
  const db = await getDatabase()
  const chunks = await db.getAllFromIndex('chunks', 'by-session', sessionId)
  return chunks.sort((a, b) => a.sequence - b.sequence)
}

/**
 * Delete a session and all its chunks.
 *
 * @param sessionId - The session ID
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const db = await getDatabase()

  // Get all chunk keys for this session
  const chunks = await db.getAllKeysFromIndex('chunks', 'by-session', sessionId)

  // Delete in transaction
  const tx = db.transaction(['sessions', 'chunks'], 'readwrite')
  const sessionsStore = tx.objectStore('sessions')
  const chunksStore = tx.objectStore('chunks')

  await sessionsStore.delete(sessionId)
  await Promise.all(chunks.map((key) => chunksStore.delete(key)))
  await tx.done
}

/**
 * Get all sessions that are not completed or failed.
 * These are sessions that were interrupted and may need recovery.
 *
 * @returns Array of incomplete sessions
 */
export async function getIncompleteSessions(): Promise<RecordingSession[]> {
  const db = await getDatabase()
  const allSessions = await db.getAll('sessions')
  return allSessions.filter((s) => s.status === 'recording' || s.status === 'uploading')
}

/**
 * Get all sessions for a specific appointment.
 *
 * @param appointmentId - The appointment ID
 * @returns Array of sessions for this appointment
 */
export async function getSessionsByAppointment(
  appointmentId: string
): Promise<RecordingSession[]> {
  const db = await getDatabase()
  return db.getAllFromIndex('sessions', 'by-appointment', appointmentId)
}

/**
 * Get all sessions for a specific form submission.
 *
 * @param formSubmissionId - The form submission ID
 * @returns Array of sessions for this form submission
 */
export async function getSessionsByFormSubmission(
  formSubmissionId: string
): Promise<RecordingSession[]> {
  const db = await getDatabase()
  return db.getAllFromIndex('sessions', 'by-form-submission', formSubmissionId)
}

/**
 * Whether a rejected IndexedDB operation failed because the browser's storage
 * quota is full. This is the one storage failure a human has to act on — the
 * user must free space before local recording backup works again.
 */
export function isQuotaExceededError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'QuotaExceededError'
}

/**
 * Request persistent storage to prevent browser from evicting data.
 * Primarily useful on iOS Safari where storage is limited.
 *
 * @returns true if persistent storage was granted
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) {
    return false
  }
  return navigator.storage.persist()
}

/**
 * Close the database connection.
 * Useful for cleanup or forcing a reconnect.
 */
export function closeDatabase(): void {
  if (dbInstance) {
    dbInstance.close()
    dbInstance = null
  }
}
